import { useState } from "react";
import { Plus, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api, errorText, BACKEND_URL } from "@/lib/api";

const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copied")).catch(() => toast.error("Copy failed"));

export const AddDeviceDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [hardware, setHardware] = useState("ds1");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/devices", { name, hardware });
      setCreated(data);
      onCreated();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = (o) => { setOpen(o); if (!o) { setCreated(null); setName(""); setHardware("ds1"); } };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogTrigger asChild>
        <Button className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold" data-testid="add-device-open-btn"><Plus className="h-4 w-4 mr-2" />Add device</Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0F141C] border-[#222D3D] max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="add-device-modal">
        <DialogHeader>
          <DialogTitle className="font-display">{created ? `Install on ${created.name}` : "Add a device"}</DialogTitle>
          <DialogDescription>{created ? "Copy the key now — it is shown only once." : "A DS-1 box or any Linux server that hosts a website."}</DialogDescription>
        </DialogHeader>
        {created ? <InstallSteps deviceKey={created.key} /> : (
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label>Device name</Label>
              <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. DS-1 Front Office" className="bg-[#07090E]" data-testid="add-device-name-input" />
            </div>
            <RadioGroup value={hardware} onValueChange={setHardware} className="grid sm:grid-cols-2 gap-3">
              {[["ds1", "DS-1 (RK3588)", "Runs alongside the DataStopDGM suite"], ["existing", "Existing hardware", "Any Linux server with Python 3.8+"]].map(([v, t, s]) => (
                <Label key={v} htmlFor={`hw-${v}`} className={`flex gap-3 items-start rounded-md border p-3 cursor-pointer transition-colors ${hardware === v ? "border-emerald-500/50 bg-emerald-500/5" : "border-[#222D3D]"}`}>
                  <RadioGroupItem id={`hw-${v}`} value={v} data-testid={`add-device-hw-${v}`} className="mt-0.5" />
                  <span><span className="block text-slate-100">{t}</span><span className="block text-xs text-slate-500 font-normal mt-0.5">{s}</span></span>
                </Label>
              ))}
            </RadioGroup>
            <Button type="submit" disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold" data-testid="add-device-submit-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create device key"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const KeyLine = ({ deviceKey }) => (
  <div className="flex gap-2">
    <code className="flex-1 font-mono text-xs text-emerald-400 bg-[#07090E] border border-[#222D3D] rounded px-3 py-2 break-all" data-testid="device-key-display">{deviceKey}</code>
    <Button size="icon" variant="outline" className="border-[#222D3D] bg-transparent shrink-0" onClick={() => copy(deviceKey)} data-testid="device-key-copy-btn"><Copy className="h-4 w-4" /></Button>
  </div>
);

export const InstallSteps = ({ deviceKey }) => {
  const script = `# 1. Install the Agent List sensor (RK3588 / ARM64 and x86)
sudo apt-get install -y tcpdump python3
sudo mkdir -p /opt/agentlist && cd /opt/agentlist
sudo curl -fsSL ${BACKEND_URL}/api/device/agent.py -o agentlist_sensor.py

# 2. Run it as a service on the gateway
sudo tee /etc/systemd/system/agentlist.service >/dev/null <<'EOF'
[Unit]
Description=Agent List sensor
After=network-online.target

[Service]
Environment=DOORMAN_URL=${BACKEND_URL}
Environment=DOORMAN_DEVICE_KEY=${deviceKey}
Environment=MODE=gateway
# Name each part of the building's network (only these names are recorded)
Environment="LAN_AREAS=10.0.10.0/24=Front desk,10.0.20.0/22=Guest Wi-Fi,10.0.5.0/24=Servers"
# Optional names for on-site systems AI agents may reach
Environment="SERVERS=10.0.5.10=Booking server,10.0.5.11=POS"
# Software validation access point for in-building software (set the DS-1's LAN address to keep it off the internet side)
Environment=TRUTH_WINDOW_PORT=8787
Environment=TRUTH_WINDOW_BIND=10.0.5.1
# Website-only alternatives: MODE=proxy (LISTEN_PORT, UPSTREAM) or MODE=log (LOG_FILE, SITE_HOST)
ExecStart=/usr/bin/python3 /opt/agentlist/agentlist_sensor.py
Restart=always

[Install]
WantedBy=multi-user.target
EOF
sudo systemctl daemon-reload && sudo systemctl enable --now agentlist`;
  return (
    <div className="space-y-4 min-w-0 w-full" data-testid="device-install-steps">
      <KeyLine deviceKey={deviceKey} />
      <div className="rounded-md border border-[#222D3D] bg-[#07090E]">
        <div className="flex items-center justify-between px-4 py-2 border-b border-[#222D3D]">
          <span className="text-xs text-slate-400">Run on the device</span>
          <Button size="sm" variant="ghost" onClick={() => copy(script)} data-testid="device-install-copy-btn"><Copy className="h-3.5 w-3.5 mr-1.5" />Copy</Button>
        </div>
        <pre className="p-4 text-[11px] font-mono text-slate-300 whitespace-pre-wrap break-all">{script}</pre>
      </div>
      <p className="text-xs text-slate-500">Gateway mode reads a filtered copy of traffic while the operating system forwards it, so the network never slows down. Only the network area names you set are recorded — never devices, guests or IP addresses. The device shows as online within a minute.</p>
    </div>
  );
};

export const KeyReveal = ({ name, deviceKey, onDone }) => (
  <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-4 space-y-3" data-testid="device-key-reveal">
    <p className="text-sm text-amber-200">New key for {name}. Update DOORMAN_DEVICE_KEY on the device — the old key has stopped working.</p>
    <KeyLine deviceKey={deviceKey} />
    <Button size="sm" variant="ghost" onClick={onDone} data-testid="device-key-reveal-done">Done</Button>
  </div>
);
